import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Search, Menu, X, ChevronDown, ShoppingBag } from "lucide-react";
import { Logo } from "./Logo";
import { useCategories } from "../lib/categoriesContext";
import { useCart } from "../lib/cart";
import { usePageContent, show } from "../lib/usePageContent";

export function Header() {
  const [open, setOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const { categories } = useCategories();
  const { totalItems } = useCart();
  const { content } = usePageContent("header");

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/recherche?q=${encodeURIComponent(q.trim())}`);
      setOpen(false);
    }
  };

  return (
    <header className="bg-[#FAF8F3] border-b-2 border-[#1A1A18] sticky top-0 z-40" data-testid="site-header">
      <div className="bg-[#1A1A18] text-[#FAF8F3] py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 font-mono-label text-[10px] flex justify-between items-center gap-4">
          <span className="hidden sm:inline opacity-80">INFORMER · COMPRENDRE · DÉBATTRE</span>
          <span className="opacity-90">DEPUIS LE 17 OCT 2024 · MAURICE</span>
          <Link to="/plaidoyer" className="text-[#C4944A] hover:underline" data-testid="header-strip-plaidoyer">NOTRE PLAIDOYER →</Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-6 h-20">
          <Link to="/" className="flex-shrink-0" data-testid="header-logo-link"><Logo size="md" /></Link>

          <nav className="hidden lg:flex items-center gap-8">
            <div className="relative" onMouseEnter={() => setCatOpen(true)} onMouseLeave={() => setCatOpen(false)}>
              <button className="font-mono-label text-[#1A1A18] hover:text-[#4A7A3D] flex items-center gap-1.5" data-testid="header-categories-button">
                Rubriques <ChevronDown size={14} />
              </button>
              {catOpen && categories.length > 0 && (
                <div className="absolute top-full left-0 pt-3" data-testid="header-categories-dropdown">
                  <div className="bg-[#FAF8F3] border-2 border-[#1A1A18] w-[640px] grid grid-cols-2 p-2">
                    {categories.map((c) => (
                      <Link key={c.slug} to={`/rubrique/${c.slug}`} className="px-4 py-3 hover:bg-[#F5EFEB] border-b border-[#E0D8CE] flex gap-3 items-start" data-testid={`header-cat-${c.slug}`}>
                        <span className="w-1 h-12 mt-0.5 flex-shrink-0" style={{ backgroundColor: c.accent_color || "#4A7A3D" }} />
                        <div>
                          <div className="font-display text-base text-[#1A1A18] leading-tight">{c.name}</div>
                          <div className="font-mono-label text-[#4A7A3D] mt-0.5">{c.tagline}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <NavLink to="/plaidoyer" className={({ isActive }) => `font-mono-label ${isActive ? "text-[#4A7A3D]" : "text-[#1A1A18]"} hover:text-[#4A7A3D]`} data-testid="header-plaidoyer">Plaidoyer</NavLink>
            <NavLink to="/ressources" className={({ isActive }) => `font-mono-label ${isActive ? "text-[#4A7A3D]" : "text-[#1A1A18]"} hover:text-[#4A7A3D]`} data-testid="header-ressources">Ressources</NavLink>
            <NavLink to="/boutique" className={({ isActive }) => `font-mono-label ${isActive ? "text-[#4A7A3D]" : "text-[#1A1A18]"} hover:text-[#4A7A3D]`} data-testid="header-boutique">Boutique</NavLink>
            <NavLink to="/a-propos" className={({ isActive }) => `font-mono-label ${isActive ? "text-[#4A7A3D]" : "text-[#1A1A18]"} hover:text-[#4A7A3D]`} data-testid="header-about">À propos</NavLink>
            <NavLink to="/contact" className={({ isActive }) => `font-mono-label ${isActive ? "text-[#4A7A3D]" : "text-[#1A1A18]"} hover:text-[#4A7A3D]`} data-testid="header-contact">Contact</NavLink>
          </nav>

          <div className="flex items-center gap-3">
            <form onSubmit={submitSearch} className="hidden md:flex items-center border-b-2 border-[#1A1A18] pb-1">
              <Search size={16} className="text-[#4B4B48]" />
              <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={content.search_placeholder || ""} className="bg-transparent ml-2 outline-none text-sm w-32 placeholder:text-[#4B4B48]" data-testid="header-search-input" />
            </form>
            <Link to="/panier" className="relative inline-flex items-center justify-center min-h-[44px] min-w-[44px] p-2 hover:text-[#4A7A3D]" aria-label="Panier" data-testid="header-cart">
              <ShoppingBag size={20} />
              {totalItems > 0 && (
                <span className="absolute top-1 right-1 bg-[#C4944A] text-[#1A1A18] font-mono-label text-[10px] w-5 h-5 flex items-center justify-center" data-testid="header-cart-count">{totalItems}</span>
              )}
            </Link>
            {show(content, "cta_soutenir_label") && content.cta_soutenir_label && (
              <Link to={content.cta_soutenir_url || "/plaidoyer"} className="hidden md:inline-flex items-center min-h-[44px] bg-[#4A7A3D] text-[#FAF8F3] px-4 py-2 font-mono-label hover:bg-[#3E6633]" data-testid="header-cta-plaidoyer">{content.cta_soutenir_label}</Link>
            )}
            <button className="lg:hidden p-2 -mr-2" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="header-menu-toggle">
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t-2 border-[#1A1A18] bg-[#FAF8F3]" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <form onSubmit={submitSearch} className="flex items-center border-b-2 border-[#1A1A18] pb-2 mb-6">
              <Search size={18} />
              <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un article, un mot-clé…" className="bg-transparent ml-2 outline-none text-base w-full" data-testid="mobile-search-input" />
            </form>
            <div className="font-mono-label text-[#4B4B48] mb-3">Rubriques</div>
            <div className="grid grid-cols-1 gap-0 mb-6">
              {categories.map((c) => (
                <Link key={c.slug} to={`/rubrique/${c.slug}`} onClick={() => setOpen(false)} className="py-3 border-b border-[#E0D8CE] flex justify-between items-baseline" data-testid={`mobile-cat-${c.slug}`}>
                  <span className="font-display text-lg">{c.name}</span>
                  <span className="font-mono-label text-[#4A7A3D]">{c.tagline}</span>
                </Link>
              ))}
            </div>
            <div className="flex flex-col gap-3">
              <Link to="/plaidoyer" onClick={() => setOpen(false)} className="bg-[#4A7A3D] text-[#FAF8F3] px-5 py-3 font-mono-label text-center" data-testid="mobile-plaidoyer">Notre plaidoyer →</Link>
              <Link to="/ressources" onClick={() => setOpen(false)} className="border-2 border-[#1A1A18] px-5 py-3 font-mono-label text-center" data-testid="mobile-ressources">Ressources</Link>
              <Link to="/boutique" onClick={() => setOpen(false)} className="border-2 border-[#1A1A18] px-5 py-3 font-mono-label text-center" data-testid="mobile-boutique">Boutique</Link>
              <Link to="/a-propos" onClick={() => setOpen(false)} className="border-2 border-[#1A1A18] px-5 py-3 font-mono-label text-center" data-testid="mobile-about">À propos</Link>
              <Link to="/contact" onClick={() => setOpen(false)} className="border-2 border-[#1A1A18] px-5 py-3 font-mono-label text-center" data-testid="mobile-contact">Contact</Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
